import { useRef, useState } from "react";
import { Loader2, ImagePlus } from "lucide-react";
import { PinkButton } from "./Buttons";
import { api, errorText } from "../lib/api";

const inputCls = "mt-2 w-full rounded-2xl border-2 border-ink bg-paper px-4 py-3 font-body text-base text-ink placeholder:text-ink/50 focus:border-pop sm:text-lg";
const ALL = ["Laughter", "Tears", "Fears"];

const Text = ({ id, label, hint, ...props }) => (
  <div>
    <label htmlFor={id} className="font-body text-base font-semibold">{label}</label>
    <input id={id} data-testid={id} className={inputCls} required {...props} />
    {hint && <p className="mt-1.5 font-body text-sm text-ink/75">{hint}</p>}
  </div>
);

const Choice = ({ name, value, checked, onChange, children, testId }) => (
  <label className={`cursor-pointer rounded-full border-2 px-4 py-2 font-body font-semibold transition-colors has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:outline-pop ${checked ? "border-ink bg-ink text-paper" : "border-ink/30 hover:border-ink"}`}>
    <input type="radio" name={name} value={value} checked={checked} onChange={onChange} data-testid={testId} className="sr-only" />
    {children}
  </label>
);

export const SubmitFilm = ({ used, onDone, defaultCreator }) => {
  const open = ALL.filter((c) => !used.includes(c));
  const empty = { title: "", creator_name: defaultCreator || "", category: open[0] || "", description: "", watch_url: "", download_url: "", used_ai: "" };
  const [f, setF] = useState(empty);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const pick = (e) => {
    const fl = e.target.files?.[0];
    if (!fl) return;
    setFile(fl);
    setPreview(URL.createObjectURL(fl));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) { setError("Add a thumbnail image."); return; }
    if (!f.used_ai) { setError("Tell us whether the film used AI."); return; }
    const body = new FormData();
    Object.entries(f).forEach(([k, v]) => body.append(k, v));
    body.append("thumbnail", file);
    setBusy(true);
    try {
      await api.post("/films", body);
      onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="submit-film-form" className="grid gap-5 rounded-[36px] border-2 border-ink bg-white p-6 shadow-[8px_8px_0_#1B1340] sm:p-9">
      <h2 className="font-head text-3xl sm:text-4xl">Submit a film</h2>
      <Text id="film-title-input" label="Film title" value={f.title} onChange={set("title")} maxLength={80} />
      <Text id="film-creator-input" label="Creator name" hint="Shown on your card." value={f.creator_name} onChange={set("creator_name")} maxLength={80} />
      <fieldset>
        <legend className="font-body text-base font-semibold">Category</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {ALL.map((c) => used.includes(c) ? (
            <span key={c} className="rounded-full border-2 border-dashed border-ink/25 px-4 py-2 font-body text-ink/60">{c} (entered)</span>
          ) : (
            <Choice key={c} name="category" value={c} checked={f.category === c} onChange={set("category")} testId={`film-category-${c.toLowerCase()}`}>{c}</Choice>
          ))}
        </div>
      </fieldset>
      <div>
        <label htmlFor="film-description-input" className="font-body text-base font-semibold">Description</label>
        <textarea id="film-description-input" data-testid="film-description-input" required maxLength={100} rows={2} value={f.description} onChange={set("description")} className={`${inputCls} resize-none`} />
        <p className="mt-1.5 text-right font-body text-sm text-ink/75" data-testid="film-description-count">{f.description.length}/100</p>
      </div>
      <Text id="film-watch-url-input" label="Watch link" type="url" placeholder="https://youtu.be/..." hint="YouTube, Vimeo or Google Drive. Up to 2:00, landscape or portrait." value={f.watch_url} onChange={set("watch_url")} />
      <Text id="film-download-url-input" label="Downloadable MP4 link" type="url" placeholder="https://we.tl/..." value={f.download_url} onChange={set("download_url")}
        hint="WeTransfer, Google Drive or Dropbox. We'll download it when we receive it, and ask you to resend if the link has expired." />
      <div>
        <span className="font-body text-base font-semibold">Thumbnail</span>
        <button type="button" data-testid="film-thumbnail-button" onClick={() => fileRef.current?.click()}
          className="mt-2 grid aspect-video w-full place-items-center overflow-hidden rounded-2xl border-2 border-dashed border-ink/40 bg-paper hover:border-ink">
          {preview ? <img src={preview} alt="Thumbnail preview" className="h-full w-full object-cover" /> : (
            <span className="flex flex-col items-center gap-2 font-body font-semibold text-ink/80"><ImagePlus size={28} /> Upload JPG, PNG or WebP (max 5 MB)</span>
          )}
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} data-testid="film-thumbnail-input" className="sr-only" tabIndex={-1} />
      </div>
      <fieldset>
        <legend className="font-body text-base font-semibold">Did this film use AI?</legend>
        <p className="mt-1 font-body text-sm text-ink/75">Private. Never shown on cards, only compared on results day.</p>
        <div className="mt-2 flex gap-2">
          <Choice name="used_ai" value="yes" checked={f.used_ai === "yes"} onChange={set("used_ai")} testId="film-ai-yes">Yes</Choice>
          <Choice name="used_ai" value="no" checked={f.used_ai === "no"} onChange={set("used_ai")} testId="film-ai-no">No</Choice>
        </div>
      </fieldset>
      {error && <p data-testid="submit-film-error" role="alert" className="rounded-xl bg-pop/15 px-4 py-2 font-body font-semibold">{error}</p>}
      <PinkButton type="submit" data-testid="submit-film-button" disabled={busy} className="w-full sm:w-auto sm:justify-self-start">
        {busy && <Loader2 size={18} className="animate-spin" />} Send it
      </PinkButton>
    </form>
  );
};
